import { useEffect, useState } from "react";

export function DeleteVideoModal({
  open,
  videoTitle,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  videoTitle: string;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}) {
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when the modal closes.
  useEffect(() => {
    if (!open) {
      setConfirmText("");
      setDeleting(false);
      setError(null);
    }
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !deleting) onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, deleting, onCancel]);

  if (!open) return null;

  const canDelete = confirmText === "DELETE" && !deleting;

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError((e as Error).message);
      setDeleting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => !deleting && onCancel()}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-lg font-semibold">Delete video?</h2>
          <p className="text-sm text-neutral-700 mt-2">
            <span className="font-medium">{videoTitle}</span> will be permanently deleted from
            Cloudflare Stream. The embed URL will stop working immediately and this cannot be
            undone.
          </p>
        </div>

        <div>
          <label className="label">
            Type <span className="font-mono font-semibold">DELETE</span> in all caps to confirm:
          </label>
          <input
            className="input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoFocus
            disabled={deleting}
            placeholder="DELETE"
          />
        </div>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end pt-2">
          <button className="btn-secondary" onClick={onCancel} disabled={deleting}>
            Cancel
          </button>
          <button className="btn-danger" onClick={handleDelete} disabled={!canDelete}>
            {deleting ? "Deleting…" : "Delete video"}
          </button>
        </div>
      </div>
    </div>
  );
}
